/* ------------------------------------------------------------------ */
/*  Decoder engine – run log lines through parsed decoders            */
/* ------------------------------------------------------------------ */

import type { WazuhDecoder, DecodedLog, ParseMessage } from './types';

/**
 * Wazuh uses its own regex syntax with (\S+) style groups.
 * We translate the most common Wazuh patterns to JS regex:
 *  - Wazuh \S+ is already valid JS regex
 *  - Wazuh uses () for capture groups like JS
 *  - Wazuh's \. is already valid
 * We wrap the whole thing in a try/catch for safety.
 */
function compileRegex(
  pattern: string,
  messages: ParseMessage[],
  context: string
): RegExp | null {
  try {
    return new RegExp(pattern);
  } catch (e) {
    messages.push({
      type: 'error',
      message: `${context}: Invalid regex "${pattern}" — ${(e as Error).message}`,
    });
    return null;
  }
}

const monthToIndex: Record<string, number> = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

/** Try to extract syslog-style program name from log line */
function extractSyslogProgram(line: string): string | undefined {
  // Standard syslog: "Mon DD HH:MM:SS hostname program[pid]: message"
  // or: "Mon DD HH:MM:SS hostname program: message"
  const syslogRe =
    /^\w{3}\s+\d{1,2}\s+\d{2}:\d{2}:\d{2}\s+\S+\s+([a-zA-Z0-9_/.-]+?)(?:\[\d+\])?:\s/;
  const m = line.match(syslogRe);
  return m ? m[1] : undefined;
}

/** Extract syslog timestamp and convert to epoch seconds (UTC, current year) */
function extractSyslogTimestampSec(line: string): number | undefined {
  const tsRe =
    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\s+(\d{2}):(\d{2}):(\d{2})\b/;
  const m = line.match(tsRe);
  if (!m) return undefined;

  const month = monthToIndex[m[1]];
  if (month === undefined) return undefined;

  const day = Number.parseInt(m[2], 10);
  const hour = Number.parseInt(m[3], 10);
  const minute = Number.parseInt(m[4], 10);
  const second = Number.parseInt(m[5], 10);

  if ([day, hour, minute, second].some((n) => Number.isNaN(n))) {
    return undefined;
  }

  const year = new Date().getUTCFullYear();
  return Math.floor(Date.UTC(year, month, day, hour, minute, second) / 1000);
}

interface CompiledDecoder {
  decoder: WazuhDecoder;
  programNameRx?: RegExp;
  prematchRx?: RegExp;
  extractRx?: RegExp;
}

function compileDecoders(
  decoders: WazuhDecoder[],
  messages: ParseMessage[]
): CompiledDecoder[] {
  const compiled: CompiledDecoder[] = [];

  for (const decoder of decoders) {
    const programNameRx = decoder.program_name
      ? compileRegex(
          decoder.program_name,
          messages,
          `Decoder "${decoder.name}" program_name`
        )
      : undefined;
    if (decoder.program_name && !programNameRx) continue;

    const prematchRx = decoder.prematch
      ? compileRegex(decoder.prematch, messages, `Decoder "${decoder.name}" prematch`)
      : undefined;
    if (decoder.prematch && !prematchRx) continue;

    const extractRx = decoder.regex
      ? compileRegex(decoder.regex, messages, `Decoder "${decoder.name}" regex`)
      : undefined;
    if (decoder.regex && !extractRx) continue;

    compiled.push({
      decoder,
      programNameRx,
      prematchRx,
      extractRx,
    });
  }

  return compiled;
}

function decodeLineCompiled(
  line: string,
  compiledDecoders: CompiledDecoder[]
): DecodedLog {
  const result: DecodedLog = {
    raw: line,
    fields: {},
  };

  const syslogProgram = extractSyslogProgram(line);
  if (syslogProgram) {
    result.program_name = syslogProgram;
  }

  const timestampSec = extractSyslogTimestampSec(line);
  if (timestampSec !== undefined) {
    result.timestampSec = timestampSec;
  }

  for (const compiled of compiledDecoders) {
    const { decoder, programNameRx, prematchRx, extractRx } = compiled;

    if (programNameRx && !programNameRx.test(result.program_name ?? '')) continue;
    if (prematchRx && !prematchRx.test(line)) continue;

    // At this point the decoder matched. First match wins.
    result.decoder = decoder.name;

    if (extractRx && decoder.order) {
      const match = line.match(extractRx);
      if (match) {
        for (let i = 0; i < decoder.order.length; i++) {
          const fieldName = decoder.order[i];
          const value = match[i + 1]; // capture groups are 1-indexed
          if (value !== undefined) {
            result.fields[fieldName] = value;
          }
        }
      }
    }

    return result;
  }

  return result;
}

/** Run a single log line through the decoder list. First match wins. */
export function decodeLine(
  line: string,
  decoders: WazuhDecoder[],
  messages: ParseMessage[]
): DecodedLog {
  const compiledDecoders = compileDecoders(decoders, messages);
  return decodeLineCompiled(line, compiledDecoders);
}

/** Decode all log lines */
export function decodeAllLines(
  lines: string[],
  decoders: WazuhDecoder[],
  messages: ParseMessage[]
): DecodedLog[] {
  const compiledDecoders = compileDecoders(decoders, messages);
  return lines.map((line) => decodeLineCompiled(line, compiledDecoders));
}
